import { useState, useCallback, useMemo } from 'react'
import { Alert, Button, GenericDialog } from '@/common'
import useI18n from '@/shared/hooks/usei18n'
import {
  ImportedGood,
  BulkUpdateImportedGoodsUpdates,
  useBulkUpdateImportedGoods
} from '@/features/cbam-reporting/hooks/importedGoods'
import toast from 'react-hot-toast'
import BulkUpdateCustomsForm from '../components/bulkUpdateCustomsForm/BulkUpdateCustomsForm'
import {
  BulkUpdateCustomsFormData,
  getInitialFormData,
  validateBulkUpdateCustomsForm,
  EMPTY_VALUE
} from '../components/bulkUpdateCustomsForm/BulkUpdateCustomsForm.schema'
import { Trans } from 'react-i18next'

export default function BulkUpdateCustomsDialog({
  isOpen,
  close,
  selectedGoods,
  organizationId,
  cbamReportId,
  onSuccess
}: {
  isOpen: boolean
  close: () => void
  selectedGoods: ImportedGood[]
  organizationId: string
  cbamReportId: string
  onSuccess: () => void
}) {
  const { t } = useI18n()
  const { mutateAsync: bulkUpdate, isPending } = useBulkUpdateImportedGoods(organizationId, cbamReportId)
  const [formData, setFormData] = useState<BulkUpdateCustomsFormData>(getInitialFormData())

  const resetForm = useCallback(() => {
    setFormData(getInitialFormData())
  }, [])

  const handleClose = useCallback(() => {
    resetForm()
    close()
  }, [close, resetForm])

  const buildUpdates = useCallback((): BulkUpdateImportedGoodsUpdates => {
    const updates: BulkUpdateImportedGoodsUpdates = {}
    if (formData.requestedCustomsProcedure) {
      updates.requestedCustomsProcedure =
        formData.requestedCustomsProcedure === EMPTY_VALUE
          ? null
          : formData.requestedCustomsProcedure
    }
    if (formData.previousCustomsProcedure) {
      updates.previousCustomsProcedure =
        formData.previousCustomsProcedure === EMPTY_VALUE ? null : formData.previousCustomsProcedure
    }
    return updates
  }, [formData])

  const isFormValid = useMemo(() => validateBulkUpdateCustomsForm(formData).success, [formData])

  const handleSubmit = useCallback(async () => {
    try {
      const updates = buildUpdates()
      await bulkUpdate({
        ids: selectedGoods.map(good => good.id),
        updates
      })
      toast.success(
        t('cbamReporting:bulkUpdate.notifications.success', { count: selectedGoods.length })
      )
      onSuccess()
      handleClose()
    } catch (error) {
      console.error('Bulk update customs failed:', error)
      toast.error(t('cbamReporting:bulkUpdate.notifications.error'))
    }
  }, [bulkUpdate, selectedGoods, buildUpdates, t, onSuccess, handleClose])

  return (
    <GenericDialog
      isOpen={isOpen}
      close={handleClose}
      title={t('cbamReporting:bulkUpdate.customsDialog.title')}
      subtitle={t('cbamReporting:bulkUpdate.customsDialog.subtitle', {
        count: selectedGoods.length
      })}
      size="2xl"
    >
      <div className="space-y-4">
        <p className="text-sm text-text-secondary">{t('cbamReporting:bulkUpdate.description')}</p>
        <Alert type="info" className="mt-4">
          <Trans
            i18nKey="cbamReporting:bulkUpdate.inwardProcessingInfo"
            components={{ bold: <b /> }}
          />
        </Alert>
        <BulkUpdateCustomsForm data={formData} onChange={setFormData} selectedGoods={selectedGoods} />
        <footer className="flex justify-end gap-2">
          <Button variant="neutral" onClick={handleClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={!isFormValid || isPending} isLoading={isPending}>
            {t('cbamReporting:bulkUpdate.actions.update', { count: selectedGoods.length })}
          </Button>
        </footer>
      </div>
    </GenericDialog>
  )
}
